import type { SheetProps } from '@hover/blueprint';
import { Body, Button, Heading, Sheet } from '@hover/blueprint';
import React from 'react';

import { useHumanReadableEditDescription } from 'src/modules/homeownerApp/components/FullscreenImage/useHumanReadableEdits';
import { MY_UPLOAD_STYLE } from 'src/modules/homeownerApp/static_values';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type {
  AiOrSampleDisplayImage,
  AiOrSampleGeneratedImage,
} from '../../types';
import { pickImage } from '../../types';
import { Skeleton } from '../Skeleton';

import { useStyleDescription } from './useStyleDescription';

export interface DesignDetailsProps extends Omit<SheetProps, 'children'> {
  image: AiOrSampleGeneratedImage | AiOrSampleDisplayImage;
}

const DesignDetails = ({ image, isOpen, ...props }: DesignDetailsProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const { description: styleDescription, loading } = useStyleDescription(image);
  const editDescriptions = useHumanReadableEditDescription(pickImage(image));
  const showStyle = image.style !== MY_UPLOAD_STYLE;

  return (
    <Sheet
      {...props}
      bodyProps={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '100',
      }}
      header={t('fullscreenImage.designDetails')}
      isClosable={false}
      isOpen={isOpen}
    >
      {showStyle && (
        <>
          <Heading size={200}>
            {t('fullscreenImage.detailsStyle', { style: image.style })}
          </Heading>
          <Skeleton isLoaded={!loading} minHeight="5rem" width="100%">
            <Body color="inherit" m="0">
              {styleDescription}
            </Body>
          </Skeleton>
        </>
      )}
      {!!editDescriptions.length && (
        <>
          <Heading size={200}>
            {t('fullscreenImage.yourCustomizations')}
          </Heading>
          {editDescriptions.map(description => (
            <Body color="inherit" key="description" m="0" whiteSpace="pre-line">
              {description}
            </Body>
          ))}
        </>
      )}
      <Button mt="400" onClick={props.onClose} variant="tertiary">
        {t('fullscreenImage.detailsClose')}
      </Button>
    </Sheet>
  );
};

export { DesignDetails };
